import { useIsDark } from '../../../hooks/useIsDark'
import { colors } from '../../../theme/colors'
import { TruncatedAxisTick } from './TruncatedAxisTick'
import {
  SNAPSHOT_ANGLED_TICK_DY,
  SNAPSHOT_AXIS_TICK_X,
} from './chart.constants'
import {
  formatCategoryAxisTickLines,
  parseChartCategoryDate,
} from './chartCategoryDate'
import {
  FLAT_CATEGORY_AXIS_LINE_HEIGHT,
  splitFlatCategoryAxisLines,
} from './chartCategoryLabel'
import { useChartColors } from './useChartColors'

/**
 * Recharts category-axis tick: when `categoryAxisIsDate` is true, formats category strings
 * with [date-fns](https://date.fns.org/v3/docs/format); otherwise raw labels. When
 * `unparseableTruncate` is set and the value is not formatted as a date, delegates to
 * `TruncatedAxisTick`.
 */

/** Match `recharts` `<Text>` defaults so custom ticks align with axis math (`tickMargin`, `y`). */
const RECHARTS_TEXT_CAP_HEIGHT_DY = '0.71em'
const RECHARTS_TEXT_LINE_HEIGHT_DY = '1em'

export interface CategoryAxisTickProps {
  x?: number
  y?: number
  /** Merged from `XAxis` (e.g. `dy={0}` on line charts). */
  dy?: number
  /** Bottom X-axis uses `middle`; merged from Recharts. */
  textAnchor?: 'start' | 'middle' | 'end' | 'inherit'
  /** Bottom axis passes `start` so `y` is the top of the label box (see Recharts `Text`). */
  verticalAnchor?: 'start' | 'middle' | 'end'
  payload?: { value?: string | number }
  fill?: string
  /** [date-fns format](https://date.fns.org/v3/docs/format). Omit for two-line `MMM` / `yyyy`. Use a quoted newline for multi-line custom ticks, e.g. `yyyy'\\n'MM`. */
  dateFormat?: string
  /**
   * When `categoryAxisIsDate` is false, or the value is not parseable as a date, optionally
   * truncate like `TruncatedAxisTick` (vertical bar / horizontal bar axes).
   */
  unparseableTruncate?: {
    maxLength: number
    angle?: number
    textAnchor?: 'start' | 'middle' | 'end' | 'inherit'
    dx?: number
    fontSize?: number
    fontWeight?: number
    fontFamily?: string
    dy?: number
  }
  /**
   * `flat` — line chart X-axis (default).
   * `angled` — vertical bar X-axis (`angle` −45°, anchor end).
   * `horizontalBarY` — horizontal bar Y-axis categories.
   */
  dateLayout?: 'flat' | 'angled' | 'horizontalBarY'
  categoryAxisIsDate?: boolean
  /** Homepage snapshot dashboard (Figma: Montserrat, neutral.0). */
  variant?: 'default' | 'snapshot'
}

const firstLineTspanDy = (
  verticalAnchor: CategoryAxisTickProps['verticalAnchor'],
): string => {
  if (verticalAnchor === 'start') return RECHARTS_TEXT_CAP_HEIGHT_DY
  return '0'
}

export const CategoryAxisTick = ({
  payload,
  x = 0,
  y = 0,
  dy,
  textAnchor: textAnchorProp = 'middle',
  verticalAnchor = 'start',
  fill = 'white',
  dateFormat,
  unparseableTruncate,
  dateLayout = 'flat',
  categoryAxisIsDate = false,
  variant = 'default',
}: CategoryAxisTickProps) => {
  const isSnapshot = variant === 'snapshot'
  const isDark = useIsDark()
  const chartColors = useChartColors()
  const snapshotTickFill = isDark
    ? colors.presets.neutral[0]
    : chartColors.rechartsAxisTick.fill
  const tickFill =
    fill ?? (isSnapshot ? snapshotTickFill : isDark ? 'white' : 'white')
  const tickFontFamily = isSnapshot
    ? SNAPSHOT_AXIS_TICK_X.fontFamily
    : undefined
  const categoryFontWeight = isSnapshot ? 500 : 600
  const textY = y + (typeof dy === 'number' ? dy : 0)
  const raw = payload?.value
  const rawInput = raw != null ? String(raw) : ''
  const parsed =
    categoryAxisIsDate && rawInput
      ? parseChartCategoryDate(rawInput)
      : undefined

  if (!parsed && unparseableTruncate) {
    return (
      <TruncatedAxisTick
        x={x}
        y={textY}
        payload={payload ? { value: rawInput } : undefined}
        maxLength={unparseableTruncate.maxLength}
        angle={unparseableTruncate.angle}
        textAnchor={unparseableTruncate.textAnchor}
        dx={unparseableTruncate.dx}
        dy={
          unparseableTruncate.dy ?? (isSnapshot ? SNAPSHOT_ANGLED_TICK_DY : 6)
        }
        fontSize={unparseableTruncate.fontSize}
        fontWeight={unparseableTruncate.fontWeight ?? categoryFontWeight}
        fontFamily={unparseableTruncate.fontFamily ?? tickFontFamily}
        textColor={tickFill}
      />
    )
  }

  if (!parsed) {
    const lines = splitFlatCategoryAxisLines(rawInput)
    return (
      <text
        x={x}
        y={textY}
        textAnchor={textAnchorProp}
        fill={tickFill}
        fontSize={10}
        fontWeight={categoryFontWeight}
        fontFamily={tickFontFamily}
      >
        {lines.map((line, i) => (
          <tspan
            key={i}
            x={x}
            dy={
              i === 0
                ? firstLineTspanDy(verticalAnchor)
                : RECHARTS_TEXT_LINE_HEIGHT_DY
            }
          >
            {line}
          </tspan>
        ))}
      </text>
    )
  }

  const lines = formatCategoryAxisTickLines(parsed, dateFormat)

  if (dateLayout === 'angled') {
    const textAnchor = 'end'
    const dx = unparseableTruncate?.dx ?? 0
    const angledDy = isSnapshot ? SNAPSHOT_ANGLED_TICK_DY : 6
    return (
      <g transform={`translate(${x + dx},${textY})`}>
        <text
          dy={angledDy}
          textAnchor={textAnchor}
          fill={tickFill}
          fontSize={10}
          fontWeight={categoryFontWeight}
          fontFamily={tickFontFamily}
          transform="rotate(-30)"
        >
          {lines.map((line, i) => (
            <tspan
              key={i}
              x={0}
              dy={i === 0 ? 0 : FLAT_CATEGORY_AXIS_LINE_HEIGHT}
            >
              {line}
            </tspan>
          ))}
        </text>
      </g>
    )
  }

  if (dateLayout === 'horizontalBarY') {
    const dx = unparseableTruncate?.dx ?? -4
    return (
      <g transform={`translate(${x + dx},${textY})`}>
        <text
          dy={6}
          textAnchor="end"
          fill={tickFill}
          fontSize={10}
          fontWeight={categoryFontWeight}
          fontFamily={tickFontFamily}
        >
          {lines.map((line, i) => (
            <tspan
              key={i}
              x={0}
              dy={i === 0 ? 0 : FLAT_CATEGORY_AXIS_LINE_HEIGHT}
            >
              {line}
            </tspan>
          ))}
        </text>
      </g>
    )
  }

  return (
    <text
      x={x}
      y={textY}
      textAnchor={textAnchorProp}
      fill={tickFill}
      fontSize={10}
      fontWeight={isSnapshot ? 500 : 600}
      fontFamily={tickFontFamily}
    >
      {lines.map((line, i) => (
        <tspan
          key={i}
          x={x}
          dy={
            i === 0
              ? firstLineTspanDy(verticalAnchor)
              : RECHARTS_TEXT_LINE_HEIGHT_DY
          }
        >
          {line}
        </tspan>
      ))}
    </text>
  )
}
